import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import {
  HttpError,
  SupplierContractRequest,
  SupplierContractResponse,
} from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import {
  createSupplierContract,
  deleteSupplierContract,
  getSupplierContract,
  getSupplierContractYearsInDelivery,
  listSupplierContracts,
  updateSupplierContract,
} from '../../../core/clients/omni-core/functions';
import { OrganizationsStore } from '../../organizations/store/organizations.store';
import {
  EnergyInterfacesStore,
  EnergyInterfaceState,
} from '../../energy-interfaces/stores/energy-interfaces.store';
import { LocationsStore } from '../../locations/store/locations.store';
import { isNotNull } from '../../../core/helper/type-helper';

export type SupplierContractData = SupplierContractResponse;

export interface SupplierContractState {
  id: string;

  data: SupplierContractData | null;

  isLoading: boolean;
  error: HttpError | unknown | null;

  isUpdateLoading: boolean;
  updateError: HttpError | unknown | null;

  isDeleteLoading: boolean;
  deleteError: HttpError | unknown | null;
  isDeleted: boolean;
}

export interface SupplierContractPage {
  page: number;

  contracts: SupplierContractData[];
  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface SupplierContractPriceRange {
  min: number;
  max: number;
}

export interface SupplierContractsFilter {
  view: 'grid' | 'table';
  fullPrice: boolean;
  data: {
    utilityType: 'ELECTRICITY' | 'GAS' | 'ALL';
    locationID: string | null;
    orderBy:
      | 'supplierName'
      | 'productName'
      | 'nextTerminationDate'
      | 'createdAt'
      | 'latestContractCheckAlertCount';
    orderDirection: 'asc' | 'desc';
    year: string;
  };
}

export const getInitialFilter = (): SupplierContractsFilter => ({
  view: 'grid',
  fullPrice: true,
  data: {
    year: new Date().getFullYear().toString(),
    utilityType: 'ALL',
    locationID: null,
    orderBy: 'createdAt',
    orderDirection: 'desc',
  },
});

export const getEnergyInterfaces = (
  supplierContract: SupplierContractData,
  energyInterfaces: EnergyInterfaceState[],
) =>
  (supplierContract.energyInterfaces ?? []).map((ei) => {
    const data = energyInterfaces.find((storeEi) => storeEi.id === ei.energyInterfaceID)?.data;
    return {
      id: ei.energyInterfaceID,
      maloID: data?.maloID,
      locationID: data?.locationID,
      label: data?.label,
    };
  });

export const getLocations = (
  supplierContract: SupplierContractData,
  energyInterfaces: EnergyInterfaceState[],
) =>
  Array.from(
    new Set(
      getEnergyInterfaces(supplierContract, energyInterfaces)
        .map((ei) => ei.locationID)
        .filter(isNotNull),
    ),
  );

@Injectable()
export class SupplierContractStore {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private locationsStore = inject(LocationsStore);

  #currentPage = signal<number>(1);
  #limit = signal<number>(10);
  #total = signal<number>(0);
  #pages = signal<Record<number, SupplierContractPage>>({});

  #singleEntries = signal<Record<string, SupplierContractState>>({});

  #isCreateLoading = signal<boolean>(false);
  #createError = signal<HttpError | unknown | null>(null);

  #yearsLoading = signal<boolean>(false);
  #yearsError = signal<HttpError | unknown | null>(null);
  #years = signal<number[] | null>(null);

  isLoading = computed(() => Object.values(this.#pages()).some((page) => page.isLoading));
  pages = computed(() => Object.values(this.#pages()));
  total = computed(this.#total);
  areAllPagesPresent = computed(() => this.#currentPage() * this.#limit() >= this.#total());
  filter = signal<SupplierContractsFilter>(getInitialFilter());
  limit = computed(this.#limit);

  isCreateLoading = computed(this.#isCreateLoading);
  createError = computed(this.#createError);

  yearsLoading = computed(this.#yearsLoading);
  yearsError = computed(this.#yearsError);
  years = computed(this.#years);

  /**
   * All loaded supplier contract entries, both from paginated pages and single loads.
   */
  allEntries = computed(() => {
    const entries: Record<string, SupplierContractData> = {};
    Object.values(this.#pages()).forEach((page) => {
      page.contracts.forEach((contract) => {
        entries[contract.id] = contract;
      });
    });
    const singleEntries = this.#singleEntries();
    Object.keys(singleEntries).forEach((id) => {
      if (!entries[id] && !singleEntries[id].isDeleted && singleEntries[id].data) {
        entries[id] = singleEntries[id].data;
      }
    });
    return entries;
  });

  private patchPage(page: number, partial: Partial<SupplierContractPage>): void {
    this.#pages.update((pages) => {
      const currentPage = pages[page] || {
        page,
        contracts: [],
        isLoading: false,
        error: null,
        isUpdateLoading: false,
        updateError: null,
        isDeleteLoading: false,
        deleteError: null,
        isDeleted: false,
      };
      return {
        ...pages,
        [page]: {
          ...currentPage,
          ...partial,
        },
      };
    });
  }

  private patchSingleEntry(id: string, partial: Partial<SupplierContractState>): void {
    this.#singleEntries.update((entries) => {
      const currentEntry = entries[id] || {
        id,
        data: null,
        isLoading: false,
        error: null,
        isUpdateLoading: false,
        updateError: null,
        isDeleteLoading: false,
        deleteError: null,
        isDeleted: false,
      };
      return {
        ...entries,
        [id]: {
          ...currentEntry,
          ...partial,
        },
      };
    });
  }

  async loadPage(page: number): Promise<void> {
    this.#currentPage.set(page);
    const limit = this.#limit();
    this.patchPage(page, {
      isLoading: true,
      error: null,
    });

    try {
      const orgaId = untracked(() => this.organizationsStore.selectedId());
      if (!orgaId) {
        throw new Error('No organization selected');
      }
      const filter = this.filter().data;
      if (!filter.year || isNaN(+filter.year)) {
        throw new Error(`no/invalid year selected - ${filter.year}`);
      }
      const res = await this.api.invoke(listSupplierContracts, {
        limit,
        offset: (page - 1) * limit,
        id: orgaId,
        utilityType: filter.utilityType === 'ALL' ? undefined : filter.utilityType,
        locationID: filter.locationID || undefined,
        orderBy: filter.orderBy || undefined,
        orderDirection: filter.orderDirection || undefined,
        excludeUnfinishedParseRequests: true,
        yearInSupply: +filter.year,
      });
      this.#total.set(res.total);
      this.patchPage(page, {
        isLoading: false,
        contracts: res.items,
      });
    } catch (error) {
      this.patchPage(page, {
        isLoading: false,
        error,
      });
    }
  }

  loadNextPage() {
    if (this.isLoading() || this.areAllPagesPresent()) {
      return;
    }
    const currentPage = this.#currentPage();
    return this.loadPage(currentPage + 1);
  }

  async loadSingleEntry(id: string): Promise<void> {
    this.patchSingleEntry(id, {
      isLoading: true,
      error: null,
    });

    try {
      const orgaId = untracked(() => this.organizationsStore.selectedId());
      if (!orgaId) {
        throw new Error('No organization selected');
      }
      const res = await this.api.invoke(getSupplierContract, {
        oid: orgaId,
        id,
      });
      this.patchSingleEntry(id, {
        isLoading: false,
        data: res,
      });
    } catch (error) {
      this.patchSingleEntry(id, {
        isLoading: false,
        error,
      });
    }
  }

  getSingleEntry(id: string): SupplierContractState | null {
    const entry = this.#singleEntries()[id];

    if (entry) {
      return entry;
    }

    const pages = this.pages();
    for (const page of pages) {
      const contract = page.contracts.find((c) => c.id === id);
      if (contract) {
        return {
          id: contract.id,
          data: contract,
          isLoading: false,
          error: null,
          isUpdateLoading: false,
          updateError: null,
          isDeleteLoading: false,
          deleteError: null,
          isDeleted: false,
        };
      }
    }

    return null;
  }

  /**
   * @deprecated Use supplier contract wizard store instead
   */
  async create(payload: SupplierContractRequest): Promise<string> {
    this.#isCreateLoading.set(true);
    this.#createError.set(null);

    try {
      const organizationId = untracked(() => this.organizationsStore.selectedId());
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const createRes = await this.api.invoke(createSupplierContract, {
        body: payload,
        id: organizationId,
      });
      await this.loadSingleEntry(createRes.id);
      this.#isCreateLoading.set(false);
      this.#createError.set(null);
      this.energyInterfacesStore.load(); // todo: remove this after store migration
      this.locationsStore.load(); // todo: remove this after store migration
      return createRes.id;
    } catch (error) {
      this.#isCreateLoading.set(false);
      this.#createError.set(error);
      throw error;
    }
  }

  /**
   * @deprecated Use supplier contract wizard store instead
   */
  async update(id: string, payload: SupplierContractRequest): Promise<void> {
    this.patchSingleEntry(id, {
      isUpdateLoading: true,
      updateError: null,
    });

    try {
      const oid = untracked(() => this.organizationsStore.selectedId());
      if (!oid) {
        throw new Error('No organization selected');
      }
      await this.api.invoke(updateSupplierContract, {
        body: payload,
        oid,
        id,
      });
      await this.loadSingleEntry(id);
      this.patchSingleEntry(id, {
        isUpdateLoading: false,
        updateError: null,
      });
    } catch (error) {
      this.patchSingleEntry(id, {
        isUpdateLoading: false,
        updateError: error,
      });
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    this.patchSingleEntry(id, {
      isDeleteLoading: true,
      deleteError: null,
    });

    try {
      const oid = untracked(() => this.organizationsStore.selectedId());
      if (!oid) {
        throw new Error('No organization selected');
      }
      await this.api.invoke(deleteSupplierContract, { oid, id });
      this.patchSingleEntry(id, {
        isDeleteLoading: false,
        deleteError: null,
        isDeleted: true,
      });
    } catch (error) {
      this.patchSingleEntry(id, {
        isDeleteLoading: false,
        deleteError: error,
      });
      throw error;
    }
  }

  onChangeFilter() {
    this.#pages.set({});
    this.loadPage(1);
  }

  reset(): void {
    this.#currentPage.set(1);
    this.#limit.set(10);
    this.#total.set(0);
    this.#pages.set({});
    this.#singleEntries.set({});
    this.#isCreateLoading.set(false);
    this.#createError.set(null);
    this.filter.set(getInitialFilter());
  }

  async loadYears() {
    this.#yearsLoading.set(true);
    this.#yearsError.set(null);

    try {
      const organizationId = untracked(() => this.organizationsStore.selectedId());
      if (!organizationId) {
        throw new Error('No organization selected');
      }
      const res = await this.api.invoke(getSupplierContractYearsInDelivery, {
        id: organizationId,
      });
      const years = res.includes(new Date().getFullYear())
        ? res
        : [...res, new Date().getFullYear()];
      this.#years.set(years.sort());
      this.#yearsLoading.set(false);
    } catch (error) {
      this.#yearsError.set(error);
      this.#yearsLoading.set(false);
    }
  }
}
